import { useFormik } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useNotificationContext, usePartnerContext } from '../../../context'
import { mutation } from '../../../graphql'
import { notifyBugsnag } from '../../../helpers'
import Input from '../../input'
import SEO from '../../seo'
import SettingsContainer from '../settings-container'

const TRANSLATION_LOCATION = 'components.settings.custom_privacy_link.'

const CustomPrivacyLinkSetting = () => {
  const { t } = useTranslation()
  const { success, error } = useNotificationContext()
  const { partner, refetchPartner } = usePartnerContext()

  const [isLoading, setIsLoading] = useState(false)

  // Parse existing components from partner app_settings
  const components = JSON.parse(partner.app_settings?.components || '{}')

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customPrivacyLink: components.customPrivacyLink || ''
    },
    validationSchema: Yup.object({
      customPrivacyLink: Yup.string()
        .url(t(TRANSLATION_LOCATION + 'input_url_validation'))
        .nullable()
    }),
    onSubmit: async ({ customPrivacyLink }, { setSubmitting }) => {
      setIsLoading(true)
      setSubmitting(true)

      try {
        // Parse existing components
        const existingComponents = JSON.parse(
          partner.app_settings?.components || '{}'
        )

        // Update components with new privacy link
        const updatedComponents = {
          ...existingComponents,
          customPrivacyLink: customPrivacyLink || undefined // Remove key if empty
        }

        // Remove undefined values to keep JSON clean
        if (!customPrivacyLink) {
          delete updatedComponents.customPrivacyLink
        }

        const app_settings = {
          ...(partner.app_settings || {}),
          components: JSON.stringify(updatedComponents)
        }

        await mutation({
          mutation: 'updatePartner',
          input: {
            id: partner.id,
            app_settings
          }
        })

        await refetchPartner()
        success(t(TRANSLATION_LOCATION + 'success_message'))
      } catch (err) {
        error(t(TRANSLATION_LOCATION + 'error_message'))
        notifyBugsnag(err)
      } finally {
        setIsLoading(false)
        setSubmitting(false)
      }
    }
  })

  // Preview text generation
  const getPreviewText = () => {
    const privacyLink = formik.values.customPrivacyLink
    const privacyText1 = t('authenticator.privacy_policy_text_1')
    const privacyText2 = t('authenticator.privacy_policy_text_2')

    if (privacyLink) {
      return (
        <>
          {privacyText1}
          <a
            href={privacyLink}
            target='_blank'
            rel='noopener noreferrer'
            className='text-blue-600 underline hover:text-blue-800'
          >
            {privacyText2}
          </a>
        </>
      )
    } else {
      return (
        <>
          {privacyText1}
          <a
            href='https://aivy.app/privacy'
            target='_blank'
            rel='noopener noreferrer'
            className='text-blue-600 underline hover:text-blue-800'
          >
            {privacyText2}
          </a>
        </>
      )
    }
  }

  return (
    <>
      <SEO title={t('seo.titles.settings_company')} />
      <SettingsContainer
        title={t(TRANSLATION_LOCATION + 'settings_container_title')}
        description={t(TRANSLATION_LOCATION + 'settings_container_description')}
        onSubmit={formik.handleSubmit}
        dirty={formik.dirty}
        isLoading={isLoading}
      >
        <div className='space-y-6'>
          <div className='max-w-lg'>
            <Input
              id='customPrivacyLink'
              label={t(TRANSLATION_LOCATION + 'input_label')}
              type='url'
              description={t(TRANSLATION_LOCATION + 'input_description')}
              placeholder={t(TRANSLATION_LOCATION + 'input_placeholder')}
              value={formik.values.customPrivacyLink}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.customPrivacyLink}
              error={formik.errors.customPrivacyLink}
              optional
            />
          </div>

          {/* Preview Section */}
          <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
            <h4 className='mb-2 text-sm font-medium text-gray-900'>
              {t(TRANSLATION_LOCATION + 'preview_title')}
            </h4>
            <p className='text-sm text-gray-700'>{getPreviewText()}</p>
          </div>
        </div>
      </SettingsContainer>
    </>
  )
}

export default CustomPrivacyLinkSetting
